import { useMatomo } from '@datapunt/matomo-tracker-react';
import { Box, CircularProgress, Fade, FormControl, Grid, MenuItem, Select, TextField, useMediaQuery, useTheme } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { Fragment, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import wordsCount from 'words-count';

import { CmsArticles, fetchCmsArticle } from '../../../../shared/helpers/cms-articles';
import { CaptureSignature } from '../../../components/CaptureSignature/capture-signature';
import FileCard from '../../../components/FileList/file-card';
import FileListGrid from '../../../components/FileList/file-list-grid';
import UploadForm from '../../../components/UploadForm/upload-form';
import {
  applicationDeleteFile,
  applicationResetFile,
  applicationSaveFile,
  applicationSetField,
  signatureUpsert
} from '../../../redux/scholarship.actions';
import {
  EnumAppDocFormat,
  EnumAppDocType,
  EnumAppSignatureFormat,
  EnumAppSignatureType,
  EnumFileStatus,
  EnumSections,
  MAppEssay,
  MAppSignature,
  MProfile
} from '../../../redux/scholarship.models';
import {
  selectEssay,
  selectLinkedChildProfile,
  selectProfilesChild,
  selectSignatureAgreement,
  selectSignatureParent
} from '../../../redux/scholarship.selectors';
import { ApplicationFormContainer } from '../../ApplicationsForm/applications-form.styles';

interface ApplicationsFormEssayProps {
  id: string;
  userId: string | undefined;
  thisSection: MAppEssay;
  scanning: boolean;
  dispatchApplicationUpdate: (id: string, section: string, field: string, value: unknown) => void;
  dispatchDeleteFile: (
    docFormat: EnumAppDocFormat,
    docType: EnumAppDocType,
    docStatus: EnumFileStatus,
    body: Record<string, unknown>,
    id: string
  ) => void;
  dispatchResetFile: (docFormat: EnumAppDocFormat, docType: EnumAppDocType) => void;
  dispatchSaveFile: (saveFile: MAppEssay | Record<string, unknown>, section: EnumAppDocType) => void;
  dispatchSaveSignature: (data: Partial<MAppSignature>) => void;
}

const ApplicationsFormEssay = ({
  id,
  userId,
  thisSection,
  scanning,
  dispatchApplicationUpdate,
  dispatchDeleteFile,
  dispatchResetFile,
  dispatchSaveFile,
  dispatchSaveSignature
}: ApplicationsFormEssayProps) => {
  const [wordCount, setWordCount] = useState<number>(thisSection?.docContent ? wordsCount(thisSection.docContent) : 0);
  const [isFetching, setIsFetching] = useState(false);
  const [childFirstName, setChildFirstName] = useState('');
  const [childLastName, setChildLastName] = useState('');
  const [data, setData] = useState(localStorage.getItem('legal') || '');
  const essay = useSelector<unknown, MAppEssay[]>(selectEssay as (state: unknown) => MAppEssay[]);
  const parentSignatures = useSelector<unknown, MAppSignature[]>(selectSignatureParent as (state: unknown) => MAppSignature[]);
  const profilesChild = useSelector<unknown, MProfile[]>(selectProfilesChild as (state: unknown) => MProfile[]);
  const linkedChildProfile = useSelector(selectLinkedChildProfile);
  const agreementSignature = useSelector<unknown, MAppSignature | undefined>(
    selectSignatureAgreement as (state: unknown) => MAppSignature | undefined
  );
  const { trackPageView } = useMatomo();

  // Track page view
  useEffect(() => {
    trackPageView({});
  }, []);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setIsFetching(true);
        const response = await fetchCmsArticle(CmsArticles.ESSAY_LEGAL_TEXT);
        setData(response);
        localStorage.setItem('legal', response || '');
        setIsFetching(false);
      } catch (e) {
        console.error(e);
        if (setData) {
          setData('Error loading');
        }
        if (setIsFetching) {
          setIsFetching(false);
        }
      }
    };

    void fetchArticle();
  }, []);

  useEffect(() => {
    const getChildName = () => {
      if (linkedChildProfile && profilesChild) {
        const thisProfile = profilesChild.find((item) => item.id === linkedChildProfile);
        setChildFirstName(thisProfile?.firstName || '');
        setChildLastName(thisProfile?.lastName || '');
      }
    };
    getChildName();
  }, [linkedChildProfile]);

  const handleFormatChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
    if (!thisSection.docType) {
      return;
    }

    dispatchResetFile(event.target.value as EnumAppDocFormat, thisSection.docType);
  };

  const handleFileDelete = (confirmation: string): void => {
    if (!thisSection.docFormat || !thisSection.docType) {
      return;
    }

    const body = {
      confirm: confirmation,
      filepath: thisSection.filePath
    };
    dispatchDeleteFile(thisSection.docFormat, thisSection.docType, EnumFileStatus.DELETED, body, id);
  };

  const handleTextboxChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
    setWordCount(wordsCount(event.target.value));
    dispatchApplicationUpdate(id, EnumSections.ESSAY, 'docContent', event.target.value);
  };

  const handleFile = (saveFile: MAppEssay | Record<string, unknown>, section: EnumAppDocType): void => {
    dispatchSaveFile(saveFile, section);
  };

  // NOTE: essaySignature is in the essay section of redux, and passed to the API to save the data to
  // the agreement signature. essaySignature is not a key stored in the database.
  // If you need to get data from the agreement signature, use useSelector(selectSignatureAgreement)
  // to retrieve the data from the actual signature
  const changeCallbackSignature = (value: {
    firstName: string;
    lastName: string;
    checked: boolean;
    signed: boolean;
    signedAgreementWording: string;
    signatureType?: string;
    signatureFormat?: string;
  }): void => {
    const updatedSignature: Partial<MAppSignature> = {
      ...agreementSignature,
      firstName: value.firstName,
      lastName: value.lastName,
      signed: value.signed,
      signatureType: value.signatureType as EnumAppSignatureType | null,
      signatureFormat: value.signatureFormat as EnumAppSignatureFormat | null,
      signedAgreementWording: data
    };
    dispatchSaveSignature(updatedSignature);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const renderEssay = () => (
    <>
      <Box pt={3} pb={3} pl={1}>
        <ApplicationFormContainer>
          <Box ml={4} mr={4} mb={3}>
            <Fragment>
              <Typography variant="h6" gutterBottom>
                Career Essay
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  Either copy the text of your child's essay and paste in the box below, or upload a legible image of the essay.
                </Grid>
                <Grid item xs={12}>
                  <FormControl variant="outlined">
                    <Select
                      id="select_essay"
                      autoWidth={true}
                      defaultValue={EnumAppDocFormat.TEXT}
                      onChange={(e) => handleFormatChange(e)}
                      name="essayFormat"
                      value={thisSection.docFormat}
                      disabled={!!thisSection.filePath || !!thisSection.docContent}
                    >
                      <MenuItem value={EnumAppDocFormat.TEXT} selected={true}>
                        Paste Essay
                      </MenuItem>
                      <MenuItem value={EnumAppDocFormat.FILE}>Upload Essay</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Fragment>
            {thisSection.docFormat === EnumAppDocFormat.FILE && (
              <Box mt={3}>
                <UploadForm
                  accept={['image/*', 'application/pdf']}
                  appId={id}
                  dataSource={essay[0]}
                  disabled={!!thisSection.filePath}
                  section={'essay'}
                  userId={userId}
                  handleFile={handleFile}
                  appendIdentifier={true}
                  identifier={`${childFirstName}${childLastName}`}
                />
                {thisSection.filePath &&
                  (isMobile ? (
                    <FileCard files={essay} scanning={scanning} handleFileDelete={handleFileDelete} />
                  ) : (
                    <FileListGrid files={essay} scanning={scanning} handleFileDelete={handleFileDelete} />
                  ))}
              </Box>
            )}
            {thisSection.docFormat === EnumAppDocFormat.TEXT && (
              <>
                <Box mt={3}>
                  <TextField
                    id="essay"
                    label="Essay should be a minimum of 200 and max of 300 words."
                    multiline
                    rows={12}
                    fullWidth
                    variant={'outlined'}
                    value={thisSection.docContent}
                    onChange={(e) => handleTextboxChange(e)}
                  />
                </Box>
                <Typography variant="caption" display="block" style={{ color: 'grey' }} gutterBottom>
                  {wordCount} words
                </Typography>
              </>
            )}
          </Box>
          <br />
          <Fragment>
            <Typography variant="body2" component="div" gutterBottom>
              <div className="lead" dangerouslySetInnerHTML={{ __html: data }} />
              <Fade in={isFetching} timeout={5}>
                <Box m="auto">
                  <CircularProgress />
                </Box>
              </Fade>
            </Typography>
            <CaptureSignature
              signedAgreementWording={data}
              parentLabel="Parent/Guardian Signature"
              childLabel="Child Name"
              disableNames={true}
              disableAll={!!agreementSignature?.signed}
              initialFirstName={agreementSignature?.firstName ?? parentSignatures[0]?.firstName ?? ''}
              initialLastName={agreementSignature?.lastName ?? parentSignatures[0]?.lastName ?? ''}
              initialChildFirstName={childFirstName}
              initialChildLastName={childLastName}
              initialChecked={agreementSignature?.signed ? agreementSignature?.signed : false}
              initialSignatureType={EnumAppSignatureType.AGREEMENT}
              initialSignatureFormat={EnumAppSignatureFormat.DIGITAL}
              initialSignatureText={
                'By checking this box, I certify, under penalties of perjury, that I have agreed to submit this Participation Consent, ' +
                'Liability Release and Indemnity Agreement electronically and that I have read, understand and agree to each of the ' +
                'terms above.'
              }
              changeCallback={changeCallbackSignature}
            />
          </Fragment>
        </ApplicationFormContainer>
      </Box>
    </>
  );

  return <div>{renderEssay()}</div>;
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchApplicationUpdate: (id: string, section: string, field: string, value: unknown): void => {
    dispatch(applicationSetField(id, section, field, value));
  },
  dispatchDeleteFile: (
    docFormat: EnumAppDocFormat,
    docType: EnumAppDocType,
    docStatus: EnumFileStatus,
    body: Record<string, unknown>,
    id: string
  ): void => {
    dispatch(applicationDeleteFile(docFormat, docType, docStatus, body, id));
  },
  dispatchResetFile: (docFormat: EnumAppDocFormat, docType: EnumAppDocType): void => {
    dispatch(applicationResetFile(docFormat, docType));
  },
  dispatchSaveFile: (saveFile: MAppEssay | Record<string, unknown>, section: EnumAppDocType): void => {
    dispatch(applicationSaveFile(saveFile, section));
  },
  dispatchSaveSignature: (data: Partial<MAppSignature>): void => {
    dispatch(signatureUpsert(data));
  }
});

export default connect(null, mapDispatchToProps)(ApplicationsFormEssay);
